import { useLocale } from '~/shared/libs/gatsby-plugin-i18n';
import icon from '@devsisters/ui-common/lib/icon';
import styled from '@emotion/styled';
import { graphql, navigate, useStaticQuery } from 'gatsby';
import React, { ChangeEventHandler } from 'react';

import { css } from '~/css';
import { useFrontmatter } from '~/shared/contexts/frontmatter';
import type { LanguageSelectorQuery } from '~/generated/types/gatsby';
import { skin } from '~/theme';

const LANGUAGES = {
  ko: '한국어',
  en: 'English',
  ja: '日本語',
  'zh-Hant': '繁體中文',
  'zh-Hans': '简体中文',
  th: 'ภาษาไทย',
} as const;

function isSupportedLocale(value: string): value is keyof typeof LANGUAGES {
  return value in LANGUAGES;
}

interface LanguageSelectorProps {
  className?: string;
}

const Container = styled('div')(
  css({
    display: 'inline-flex',
    alignItems: 'center',
    position: 'relative',
  }),
);

const Select = styled('select')(
  css({
    textAlignLast: 'center',
    fontSize: 'inherit',
    color: 'inherit',
    background: 'inherit',
    border: 'none',
    outline: 'none',
    appearance: 'none',
    paddingLeft: [15, 15, 0],
    paddingRight: 30,
    paddingY: 5,
    cursor: 'pointer',
  }),
);

const DownIcon = styled(
  icon('0 0 69 60', <path d='M34.4908 59.8102L0.152588 0.342041H68.829L34.4908 59.8102Z' fill='black' />),
)(
  skin({
    blackSquare: css({
      marginLeft: '0.5em',
      fontSize: '0.5em',
      pointerEvents: 'none',
      position: 'absolute',
      top: '50%',
      transform: 'translateY(-50%)',
      right: 10,
    }),
  }),
);

const LanguageSelector: React.FC<LanguageSelectorProps> = ({ className }) => {
  const data = useStaticQuery<LanguageSelectorQuery>(graphql`
    query LanguageSelector {
      allSitePage {
        nodes {
          path
        }
      }
    }
  `);

  const { gameId } = useFrontmatter();
  const currentLocale = useLocale();
  const locales = Array.from(
    new Set(
      data.allSitePage.nodes
        .map((node) => node.path.match(new RegExp(`^/([^/]+)/${gameId}/$`)))
        .map((match) => match?.[1])
        .filter((locale): locale is string => Boolean(locale))
        .filter(isSupportedLocale),
    ),
  );

  const handleChange: ChangeEventHandler<HTMLSelectElement> = (e) => {
    const locale = e.currentTarget.value;
    navigate(`/${locale}/${gameId}/`, { replace: true });
  };

  return (
    <Container className={className}>
      <Select defaultValue={currentLocale} onChange={handleChange} className='language-selector'>
        {locales.map((locale) => (
          <option key={locale} value={locale}>
            {LANGUAGES[locale]}
          </option>
        ))}
      </Select>
      <DownIcon />
    </Container>
  );
};

export default LanguageSelector;
